/**
 * The client-side rules for the two forms that set a password: accepting an
 * invitation and completing a reset.
 *
 * Extracted so both components share one implementation and it can be tested
 * without TestBed. The alternative -- a getter on each component -- is how the
 * bug below survived: the rule existed twice and was surfaced nowhere.
 *
 * ## The bug this replaces
 *
 * The submit button was `[disabled]="!canSubmit"`, and canSubmit also required
 * `password.length >= minLength`. The length rule appeared in the template only
 * as an explanatory note, never as an error. So a short password produced a
 * button that could not be pressed and no message saying why: from the outside,
 * clicking Save did nothing whatsoever.
 *
 * The button is now live unless a request is in flight, and submitting reports
 * every broken rule. Being told why is strictly better than a control you
 * cannot press for reasons you have to infer.
 */

export interface PasswordFormState {
    password: string;
    repeat: string;
    /** From GET /web/auth/providers, so a deployment can raise it. */
    minLength: number;
    /**
     * Whether the template's own live mismatch alert is currently visible. It
     * appears as soon as the repeat field has anything in it, so repeating that
     * message here would print it twice.
     */
    mismatchShown: boolean;
}

/** Translation keys for every broken rule. Empty means submit. */
export function passwordProblems(state: PasswordFormState): string[] {
    const problems: string[] = [];
    const password = state.password || '';
    const repeat = state.repeat || '';

    if (password.length < state.minLength) {
        problems.push('AUTH.PASSWORD_TOO_SHORT');
    }
    if (password !== repeat && !state.mismatchShown) {
        problems.push('USER.PASSWORD_MISMATCH');
    }
    return problems;
}

/** True once the repeat field has content that does not match. */
export function isMismatch(password: string, repeat: string): boolean {
    return !!repeat && password !== repeat;
}
